'use client';

import React from 'react';
import { cn } from '../lib/utils';

export interface FieldLabelProps {
  children: React.ReactNode;
  /** Required controls carry aria-required; only optional fields get a visible marker. */
  required?: boolean;
  optional?: boolean;
  /** HTML for attribute — links to the input's id */
  htmlFor?: string;
  className?: string;
}

// Normalise common field labels without lowercasing proper names or initialisms.
const fieldLabels: Record<string, string> = {
  'First Name': 'First name',
  'Last Name': 'Last name',
  'Middle Name': 'Middle name',
  'Preferred Name': 'Preferred name',
  'Date of Birth': 'Date of birth',
  'Email Address': 'Email address',
  'Phone Number': 'Phone number',
  'Mobile Number': 'Mobile number',
  'Postal Address': 'Postal address',
  'Contact Name': 'Contact name',
  'Registration Number': 'Registration number',
};
function sentenceCaseFieldLabel(label: string) {
  const clean = label.replace(/\s*\((?:required|optional)\)\s*$/i, '');
  return fieldLabels[clean] ?? clean;
}

/**
 * Shared form field label — replaces 64+ duplicate `FieldLabel` definitions
 * across the Statura Care dashboard.
 *
 * Sentence-case label above its control, using the body role.
 */
export function FieldLabel({
  children,
  required,
  optional = required === false,
  htmlFor,
  className,
}: FieldLabelProps) {
  return (
    <label
      htmlFor={htmlFor}
      className={cn(
        'mb-1.5 block min-w-0 break-words font-body text-body font-medium text-text-heading [overflow-wrap:anywhere]',
        className,
      )}
    >
      {typeof children === 'string' ? sentenceCaseFieldLabel(children) : children}
      {!required &&
        (optional || (typeof children === 'string' && /\(optional\)\s*$/i.test(children))) && (
          <>
            {' '}
            <span className="font-normal text-text-muted">(optional)</span>
          </>
        )}
    </label>
  );
}
